import mapAxios from "axios";

import CLIENT from "../../lib/api/client";
import ENDPOINTS from "../../lib/api/endpoints";
import { GOOGLE_MAP_KEY } from "../../utils/configs";
import { FALLBACK_IP_INFO } from "../../utils/constants";

/**
 * Geography moved under `/api/geography/` and is the one domain that
 * paginates 1-based with `page_size`; `page_size=-1` returns everything.
 *
 * Verified against dev on 2026-09-16: states and cities answer with a Spring
 * page envelope (NOT the bare array the API docs describe), and `stateCode`
 * IS populated on Ghana regions, contrary to the documented defect.
 *
 * These wrappers keep the legacy `{ data: { states } }` / `{ data: { cities } }`
 * shape and expose `code` alongside `stateCode`, so existing callers such as
 * `getStateName` in common-utils keep working unchanged.
 */
const ALL_PAGES = { page: 1, page_size: -1 };

const pageItems = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;

  return [];
};

const withLegacyCode = (row) => ({ ...row, code: row.stateCode ?? row.code });

export const fetchCountryStates = async (countryCode) => {
  const { data } = await CLIENT.get(ENDPOINTS.GEOGRAPHY.states, {
    params: { country_code: countryCode, ...ALL_PAGES },
  });

  return { data: { states: pageItems(data).map(withLegacyCode) } };
};

export const fetchCities = async (countryCode, stateCode) => {
  const { data } = await CLIENT.get(ENDPOINTS.GEOGRAPHY.cities, {
    params: { country_code: countryCode, ...ALL_PAGES },
  });

  // Filtered here rather than by param: cities rows carry `stateCode`, so this
  // holds whether or not the endpoint honours a state filter.
  const cities = pageItems(data)
    .filter((city) => !stateCode || city.stateCode === stateCode)
    .map(withLegacyCode);

  return { data: { cities } };
};

export const getLocation = async ({ lat, long }) => {
  return await mapAxios.get(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${long}&key=${GOOGLE_MAP_KEY}`
  );
};

export const getLocationAddress = async ({ lat, long }) => {
  return await CLIENT.get(ENDPOINTS.ADDRESS.fromLocation, {
    params: { latitude: lat, longitude: long },
  });
};

/**
 * `/ip-info` returns 404 on this backend and has no replacement, so this
 * resolves the static default instead of making a request. Kept as a promise
 * so the existing call sites are unchanged.
 *
 * Note: `/addresses/from-location/` is also currently a 500 on dev (the
 * server-side Google key is unset), so the browser geolocation path in
 * `map-utils.js` is the reliable one.
 */
export const getIPInfo = async () => ({ data: FALLBACK_IP_INFO });
